/* Export der Spielerliste (CSV und PDF) — reine Funktionen, kein DOM, keine Texte.
 *
 * Was hier steht: wer exportieren darf, welche Spieler je Umfang dazugehören, welche Spalten es gibt,
 * wie sortiert wird und wie eine CSV-Datei aufgebaut und abgesichert wird. Das PDF zeichnet
 * export/playerListPdf.js, geladen und ausgelöst wird alles in composables/usePlayerListExport.js.
 *
 * Datenschutz: Die Spalten sind eine Positivliste (COLUMNS). Verträge, Gehälter, Ablöse, Ausweis- und
 * Visadaten, Dokumente, Trial-Berichte, Kontaktdaten, Transfermarkt-Links und X11-Daten kommen hier
 * nicht vor und können auch nicht versehentlich mitwandern, weil nur die benannten Felder gelesen
 * werden. Der Export liest nur das, was die angemeldete Person ohnehin geladen hat.
 *
 * Reihenfolge der Positionsgruppen: wie in der Kaderliste der App (POSITION_GROUPS: Angriff zuerst,
 * Tor zuletzt). Soll der Export anders sortieren, EXPORT_GROUP_ORDER ändern.
 */

import { fmtDate, ageFromDob } from './format.js';
import { positionGroup, POSITION_GROUPS, defenseRank, FEET } from './positions.js';
import { isOutOfSquad } from './status.js';
import { playerClub, transferClub } from './footballClub.js';
import { ageGroupOf } from './ageGroups.js';

/* ------------------------------------------------------------------ Rechte */

/**
 * Wer die Liste exportieren darf. Entscheidung Bastian, 2026-10-14 (ersetzt die vom 2026-10-05, nach der nur
 * admin, board und data exportieren durften): Staff nutzt Markieren und Export als Hauptwerkzeug in der
 * Spieltagsplanung. Staff darf aber NUR die Umfänge "Kader" und "Auswahl" (exportScopesFor) — nie Transfers
 * laden oder exportieren, denn Transfers sind für staff ein gesperrter Bereich.
 * Guest (und alles Unbekannte) darf nichts: Gäste lesen nur die öffentliche Ansicht ohne Geburtsdatum.
 * Ordnung, keine Sicherheit: Was die Rolle lesen darf, setzt die Datenbank durch (staff liest die Tabelle
 * players samt Geburtsdatum, aber keine Transfers); dieser Schalter regelt, wer daraus eine Datei macht.
 * EINE Quelle für die Rollenprüfung — Knopf, Menü, Dialog und Ablauf fragen hier.
 */
export const EXPORT_ROLES = ['admin', 'board', 'data', 'staff'];

/** Rollen mit allen Umfängen (inklusive Transferierte). */
export const FULL_EXPORT_ROLES = ['admin', 'board', 'data'];

/** Darf diese Rolle die Spielerliste exportieren? Unbekannte oder fehlende Rolle: nein. */
export function canExportPlayerList(role) {
  return EXPORT_ROLES.includes(role);
}

/**
 * Welche Umfänge darf diese Rolle exportieren?
 *   admin, board, data -> Kader, Transferiert, Kader + Transferiert und die Auswahl
 *   staff              -> nur Kader und die Auswahl (nie Transfers)
 *   guest, unbekannt   -> nichts
 */
export function exportScopesFor(role) {
  if (FULL_EXPORT_ROLES.includes(role)) return [...EXPORT_SCOPES, SELECTION_SCOPE];
  if (role === 'staff') return ['squad', SELECTION_SCOPE];
  return [];
}

/* ---------------------------------------------------------------- Umfänge */

/**
 * Die Auswahlen des Exports. EINE Stelle für eine neue Liste (z. B. AFH Partners): Eintrag ergänzen
 * (welche Gruppen, Dateiname-Teil), Texte playerExport.scope.<key> und scopeHint.<key> in den Sprachdateien,
 * ggf. Filter in selectPlayers. Reihenfolge = Reihenfolge im Dialog.
 *   squad            = Grassrunners-Kader (ohne Transferierte)
 *   transferred      = nur Transferierte (status = transferred)
 *   squadTransferred = Kader + Transferierte
 * Freigegebene (released) sind in keiner Auswahl enthalten.
 *
 * "selection" (Markier-Modus, C1) gehört NICHT zu EXPORT_SCOPES: Sie erscheint nicht in der Liste des
 * Export-Dialogs aus dem Konto-Menü, sondern nur, wenn der Dialog aus der Aktionsleiste geöffnet wird.
 * `byIds` = nur die übergebenen Spieler-IDs (selectPlayers, dritter Parameter); ohne IDs ist sie leer.
 */
export const SCOPE_DEFS = {
  squad: { squad: true, transferred: false, filePart: 'grassrunners' },
  transferred: { squad: false, transferred: true, filePart: 'transferred' },
  squadTransferred: { squad: true, transferred: true, filePart: 'grassrunners-mit-transfers' },
  selection: { squad: true, transferred: false, filePart: 'selection', byIds: true },
};
/** Umfänge der Liste im Export-Dialog (ohne "selection") — unverändert gegenüber vorher. */
export const EXPORT_SCOPES = Object.keys(SCOPE_DEFS).filter((k) => !SCOPE_DEFS[k].byIds);
export const SELECTION_SCOPE = 'selection';
/** Alle Umfänge, die der Ablauf annimmt. */
export const RUN_SCOPES = Object.keys(SCOPE_DEFS);
export const EXPORT_FORMATS = ['csv', 'pdf'];

export const DEFAULT_SCOPE = 'squad';

/** Definition eines Umfangs; unbekannter Umfang verhält sich wie "squad". */
function scopeDef(scope) {
  return SCOPE_DEFS[scope] || SCOPE_DEFS[DEFAULT_SCOPE];
}
export const DEFAULT_FORMAT = 'pdf';

/** Gruppenreihenfolge im Export — Quelle ist die Kaderliste der App. */
export const EXPORT_GROUP_ORDER = POSITION_GROUPS;

/* ----------------------------------------------------------------- Spalten */

const POSITION_PLACEHOLDER = 'Position TBD';

/** Alle Werte werden als Text ausgegeben; fehlende Angaben sind '' (nie "undefined" oder "null"). */
function text(v) {
  return v == null ? '' : String(v).trim();
}

/** Alter zum Stichtag. Nur für ISO-Datum (JJJJ-MM-TT); alles andere ergibt '' statt "NaN". */
function ageText(dob, now) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(text(dob))) return '';
  const age = ageFromDob(text(dob), now);
  return Number.isFinite(age) && age >= 0 ? String(age) : '';
}

/**
 * Die Spalten, in der Reihenfolge der Ausgabe. EINE Stelle zum Ändern: Spalte ergänzen, streichen
 * oder umordnen — CSV und PDF folgen. `key` ist zugleich der Schlüssel der Überschrift
 * (playerExport.col.<key> in den Sprachdateien). `weight` = relative Breite im PDF,
 * `align` = Ausrichtung im PDF. `only: 'transferred'` = Spalte nur im Abschnitt der Transferierten,
 * `not: 'transferred'` = Spalte entfällt im Abschnitt der Transferierten (Entscheidung Bastian, 2026-10-06:
 * "Bei Grassrunners seit" ist für transferierte Spieler nicht mehr relevant).
 * `value(p, ctx)` bekommt den Spieler und { now, transfer } und liefert den Text.
 */
export const COLUMNS = [
  { key: 'lastName', weight: 28, value: (p) => text(p.lastName) },
  { key: 'firstName', weight: 26, value: (p) => text(p.firstName) },
  { key: 'position', weight: 32, value: (p) => (text(p.pos) === POSITION_PLACEHOLDER ? '' : text(p.pos)) },
  { key: 'ageGroup', weight: 16, value: (p) => ageGroupOf(p) },
  { key: 'dob', weight: 24, value: (p) => fmtDate(text(p.dob)) },
  { key: 'age', weight: 13, align: 'right', value: (p, ctx) => ageText(p.dob, ctx.now) },
  { key: 'height', weight: 18, align: 'right', value: (p) => text(p.height) },
  { key: 'foot', weight: 20, value: (p) => text(p.foot) },
  { key: 'nationality', weight: 36, value: (p) => text(p.nationality) },
  { key: 'since', weight: 34, not: 'transferred', value: (p) => fmtDate(text(p.atGrassrunnersSince)) },
  { key: 'toClub', weight: 38, only: 'transferred', value: (p, ctx) => text(ctx.transfer && ctx.transfer.toClub) },
  { key: 'transferDate', weight: 26, only: 'transferred', value: (p, ctx) => fmtDate(text(ctx.transfer && ctx.transfer.date)) },
];

/**
 * Spalten eines Abschnitts: 'squad' ohne die Transferspalten; 'transferred' mit den beiden Transferspalten
 * und OHNE "since"; 'all' = alle Spalten (die zusammenhängende CSV-Tabelle: "since" bleibt für Kaderzeilen,
 * ist bei Transferierten leer, die Transferspalten sind bei Kaderzeilen leer).
 */
export function columnsFor(kind) {
  if (kind === 'all') return COLUMNS.slice();
  return COLUMNS.filter((c) => (!c.only || c.only === kind) && c.not !== kind);
}

/* ------------------------------------------------------ Spalten im PDF (Hochformat) */

/** Höhe als Text: eine reine Zahl bekommt die Einheit ("195" -> "195 cm"), alles andere bleibt, wie es ist. */
function heightWithUnit(h) {
  const s = text(h);
  return /^\d{2,3}(\.\d+)?$/.test(s) ? `${s} cm` : s;
}

/**
 * Bevorzugter Fuß im PDF: bekannte Werte (FEET: Right, Left, Both) einheitlich mit großem Anfangsbuchstaben ("right" -> "Right"),
 * alles andere unverändert. Nur die Anzeige im PDF — die gespeicherten Daten und die CSV bleiben, wie sie sind.
 */
export function footForPdf(foot) {
  const s = text(foot);
  return FEET.find((f) => f.toLowerCase() === s.toLowerCase()) || s;
}

/** Geburtsdatum mit Alter in Klammern ("06.07.2008 (18)"); ohne Alter nur das Datum, ohne Datum leer. */
function bornWithAge(dob, age) {
  if (!dob) return '';
  return age ? `${dob} (${age})` : dob;
}

/**
 * Die Spalten des PDFs (A4 hoch): weniger und breitere als die CSV-Tabelle (COLUMNS), damit nichts abgeschnitten wird.
 * Sie werden AUS den Zellen der Tabelle gebildet (buildExportTable), nicht aus neuen Daten: Reihenfolge, Inhalt und
 * Zählung der Zeilen bleiben die der CSV. `value(c)` bekommt die Zellen einer Zeile nach COLUMNS-Schlüsseln und liefert
 * { text, sub? }: `text` die Hauptzeile (Name: Nachname fett), `sub` die kleinere Zeile darunter.
 * `weight` = relative Breite (F15b: aus der Breite der längsten üblichen Werte bei 8,8 pt ermittelt; die Namensspalte bekommt in beiden
 * Listen mehr als ein Drittel der Tabelle, alle übrigen außer Receiving club und dem Namen brechen bei üblichen Werten nicht um).
 * `only` / `not` wie bei COLUMNS. `label` ist fest Englisch (das PDF geht an internationale Empfänger, wie die Player Card).
 */
export const PDF_COLUMNS = [
  {
    key: 'name', label: 'Name', weight: 64,
    value: (c) => ({ text: c.lastName, sub: [c.firstName, c.position].filter(Boolean).join(' · ') }),
  },
  { key: 'ageGroup', label: 'Age group', weight: 10, value: (c) => ({ text: c.ageGroup }) },
  { key: 'born', label: 'Born', weight: 25.5, value: (c) => ({ text: bornWithAge(c.dob, c.age) }) },
  { key: 'height', label: 'Height', weight: 14, value: (c) => ({ text: heightWithUnit(c.height) }) },
  { key: 'foot', label: 'Foot', weight: 11, value: (c) => ({ text: footForPdf(c.foot) }) },
  { key: 'nationality', label: 'Nationality', weight: 19, value: (c) => ({ text: c.nationality }) },
  { key: 'since', label: 'At Grassrunners since', weight: 22, not: 'transferred', value: (c) => ({ text: c.since }) },
  { key: 'toClub', label: 'Receiving club', weight: 23, only: 'transferred', value: (c) => ({ text: c.toClub }) },
  { key: 'transferDate', label: 'Transfer date', weight: 19, only: 'transferred', value: (c) => ({ text: c.transferDate }) },
];

/** PDF-Spalten eines Abschnitts ('squad' | 'transferred'). */
export function pdfColumnsFor(kind) {
  return PDF_COLUMNS.filter((c) => (!c.only || c.only === kind) && c.not !== kind);
}

/**
 * Eine Zeile eines Abschnitts (buildExportTable) als PDF-Zellen { text, sub? } in der Reihenfolge von pdfColumnsFor.
 * Fehlende Angaben ergeben leere Zellen, nie "undefined".
 */
export function pdfCells(section, row) {
  const byKey = Object.fromEntries(section.columns.map((col, i) => [col.key, row[i] ?? '']));
  return pdfColumnsFor(section.kind).map((col) => {
    const v = col.value(byKey);
    return { text: text(v.text), sub: text(v.sub) };
  });
}

/* --------------------------------------------------------------- Auswahl */

/**
 * Welcher Eintrag der Transferhistorie gilt als "aufnehmender Verein"? Der neueste echte Wechsel
 * (Transfer oder Leihe) mit Vereinsnamen; das Ende einer Leihe ist kein Wechsel zu einem neuen Verein.
 * Ohne passenden Eintrag: aktueller Verein des Spielers, ohne Datum. Die Historie kennt keine Beträge.
 * Klubregister: Der Name kommt aus dem Register (Verweis), sonst aus dem alten Text; ohne `byId` (Register nicht
 * geladen) gilt der Text wie bisher.
 *
 * @param {Array} history  Einträge wie rowToTransfer (domain/transfers.js), neueste zuerst
 * @param {Map|null} [byId] Vereine des Registers (domain/footballClub.js clubsById)
 * @returns {{toClub: string, date: string}}
 */
export function receivingTransfer(player, history, byId = null) {
  const sorted = (history || []).slice().sort((a, b) => text(b.date).localeCompare(text(a.date)));
  const named = (t) => text(transferClub(byId, t, 'to').name);
  const hit = sorted.find((t) => (t.kind === 'permanent' || t.kind === 'loan') && named(t));
  if (hit) return { toClub: named(hit), date: text(hit.date) };
  return { toClub: text(playerClub(byId, player).name), date: '' };
}

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

function byName(a, b) {
  return collator.compare(text(a.lastName), text(b.lastName)) || collator.compare(text(a.firstName), text(b.firstName));
}

/**
 * Nach Positionsgruppe (Reihenfolge EXPORT_GROUP_ORDER), in Defense nach Einzelposition (positions.js DEFENSE_ORDER),
 * dann Nachname, dann Vorname. Verändert die Eingabe nicht.
 */
export function sortForExport(players) {
  const rank = (p) => EXPORT_GROUP_ORDER.indexOf(positionGroup(p.pos));
  const inGroup = (p) => (positionGroup(p.pos) === 'def' ? defenseRank(p.pos) : 0);
  return players.slice().sort((a, b) => rank(a) - rank(b) || inGroup(a) - inGroup(b) || byName(a, b));
}

/**
 * Welche Spieler gehören zu welchem Umfang? Quelle ist die geladene Liste (also nur, was die Person
 * ohnehin sehen darf). Kader = alles außer transferiert/freigegeben — dasselbe wie die Spalte
 * "Grassrunners Kader" der App (usePlayers.squad). Freigegebene sind nie dabei.
 *
 * @param {Iterable} [ids]  nur für Umfänge mit byIds ("selection"): die markierten Spieler-IDs. Fehlt sie, ist
 *                         die Auswahl leer. Für die übrigen Umfänge ohne Wirkung (sie verhalten sich wie vorher).
 * @returns {{ squad: Array, transferred: Array }}  beide sortiert; je nach Umfang ist eine Gruppe leer
 */
export function selectPlayers(players, scope, ids = null) {
  let list = (players || []).filter((p) => p && !p._publicOnly);
  const def = scopeDef(scope);
  if (def.byIds) {
    const set = new Set(ids || []);
    list = list.filter((p) => set.has(p.id));
  }
  const squad = def.squad ? sortForExport(list.filter((p) => !isOutOfSquad(p.status))) : [];
  const transferred = def.transferred
    ? sortForExport(list.filter((p) => p.status === 'transferred'))
    : [];
  return { squad, transferred };
}

/**
 * Tabelle für CSV und PDF.
 *
 * @param {Array} players  geladene Spieler
 * @param {object} opts
 * @param {string} opts.scope                     Schlüssel aus SCOPE_DEFS
 * @param {number} [opts.now]                     Stichtag für das Alter (Millisekunden)
 * @param {Map<string, Array>} [opts.transfers]   Spieler-ID -> Transferhistorie (nur für Transferierte nötig)
 * @param {Iterable} [opts.ids]                   markierte Spieler-IDs (nur Umfang "selection")
 * @returns {{
 *   scope: string, total: number,
 *   sections: Array<{ kind: 'squad'|'transferred', group: string|null, columns: Array, rows: string[][] }>
 * }}
 *   squad-Abschnitte: einer je Positionsgruppe (group = 'st' | 'wing' | 'mid' | 'def' | 'gk' | 'open');
 *   transferred: ein Abschnitt (group = null).
 */
export function buildExportTable(players, { scope = DEFAULT_SCOPE, now = Date.now(), transfers = new Map(), clubsById = null, ids = null } = {}) {
  const { squad, transferred } = selectPlayers(players, scope, ids);
  const sections = [];

  const squadCols = columnsFor('squad');
  for (const group of EXPORT_GROUP_ORDER) {
    const members = squad.filter((p) => positionGroup(p.pos) === group);
    if (!members.length) continue;
    sections.push({
      kind: 'squad', group, columns: squadCols,
      rows: members.map((p) => squadCols.map((c) => c.value(p, { now }))),
    });
  }

  if (transferred.length) {
    const cols = columnsFor('transferred');
    sections.push({
      kind: 'transferred', group: null, columns: cols,
      rows: transferred.map((p) => {
        const t = receivingTransfer(p, transfers.get(p.id), clubsById);
        return cols.map((c) => c.value(p, { now, transfer: { toClub: t.toClub, date: t.date } }));
      }),
    });
  }

  return { scope, total: squad.length + transferred.length, sections };
}

/* -------------------------------------------------------------------- CSV */

/** Excel führt Zellen als Formel aus, die mit = + - @ (oder Tab/CR) beginnen — dann ein ' voranstellen. */
const FORMULA_START = /^[=+\-@\t\r]/;

/**
 * Eine Zelle für CSV: gegen Formelinjektion absichern, bei Semikolon, Anführungszeichen oder
 * Zeilenumbruch in Anführungszeichen setzen (Anführungszeichen verdoppeln).
 */
export function csvCell(value) {
  let s = value == null ? '' : String(value);
  if (FORMULA_START.test(s)) s = `'${s}`;
  if (/[;"\r\n]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

export const CSV_BOM = '﻿';
export const CSV_SEPARATOR = ';';
export const CSV_EOL = '\r\n';

/**
 * CSV-Text: UTF-8-BOM vorweg (damit deutsches Excel die Umlaute richtig liest), Semikolon als Trenner,
 * Zeilenende CRLF. Bei Kader + Transferierte steht eine Spalte "Gruppe" vorn und die beiden
 * Transferspalten gelten für alle Zeilen (bei Kaderspielern leer) — eine zusammenhängende Tabelle, die
 * sich in Excel sortieren und filtern lässt. Bei nur Kader gibt es die Gruppenspalte nicht.
 *
 * @param {ReturnType<typeof buildExportTable>} table
 * @param {{ header: (key: string) => string, group: (kind: 'squad'|'transferred') => string }} labels
 */
export function buildCsv(table, labels) {
  const withTransfers = table.sections.some((s) => s.kind === 'transferred');
  /* Gruppenspalte nur, wenn Kader und Transferierte in einer Tabelle stehen */
  const def = scopeDef(table.scope);
  const withGroup = def.squad && def.transferred;
  /* Nur Transferierte: die Spalten des Transfer-Abschnitts, ohne "since" */
  const kind = withGroup ? 'all' : def.transferred ? 'transferred' : withTransfers ? 'all' : 'squad';
  const cols = columnsFor(kind);
  const lines = [];

  lines.push([...(withGroup ? [labels.header('group')] : []), ...cols.map((c) => labels.header(c.key))]);

  for (const section of table.sections) {
    for (const row of section.rows) {
      /* Zellen nach Schlüssel zuordnen: Kaderzeilen haben die Transferspalten nicht, Transferierte nicht "since". */
      const byKey = Object.fromEntries(section.columns.map((c, i) => [c.key, row[i]]));
      lines.push([
        ...(withGroup ? [labels.group(section.kind)] : []),
        ...cols.map((c) => byKey[c.key] ?? ''),
      ]);
    }
  }

  return CSV_BOM + lines.map((l) => l.map(csvCell).join(CSV_SEPARATOR)).join(CSV_EOL) + CSV_EOL;
}

/* -------------------------------------------------------------- Dateiname */

function pad(n) {
  return String(n).padStart(2, '0');
}

/** Lokales Datum JJJJ-MM-TT (nicht UTC: kurz nach Mitternacht in Nigeria soll das heutige Datum stehen). */
export function localIsoDate(d = new Date()) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Höchstlänge des freien Listentitels (Zeichen). */
export const LIST_TITLE_MAX = 60;

/**
 * Freitext-Titel bereinigen: Steuerzeichen und Zeilenumbrüche raus, Leerraum zusammenziehen, auf
 * LIST_TITLE_MAX kürzen. Leer bleibt leer (dann gilt der Standardtitel).
 */
export function sanitizeListTitle(title) {
  return String(title == null ? '' : title)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, LIST_TITLE_MAX)
    .trim();
}

/** Dateinamenteil aus einem Titel: ohne Akzente, klein, nur a-z0-9 und "-". Leer, wenn nichts übrig bleibt. */
export function titleSlug(title) {
  return sanitizeListTitle(title)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * afh-spielerliste-<grassrunners | grassrunners-mit-transfers | transferred | selection>-JJJJ-MM-TT.csv|pdf.
 * Mit Titel (nur wenn nach dem Bereinigen etwas übrig bleibt): <titel>-JJJJ-MM-TT.csv|pdf.
 */
export function exportFilename(scope, format, date = new Date(), title = '') {
  const ext = format === 'csv' ? 'csv' : 'pdf';
  const slug = titleSlug(title);
  if (slug) return `${slug}-${localIsoDate(date)}.${ext}`;
  return `afh-spielerliste-${scopeDef(scope).filePart}-${localIsoDate(date)}.${ext}`;
}
